import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Input, Select, useToast} from "uikit";
import Progress from './components/Progress';
import SwitcherGroup from "./components/SwitcherGroup";
import GroupRow from "./components/GroupRow";
import styles from './Form.module.scss'
import {FormPropsInterface} from "@/views/Admin/Events/Event/Promocodes/Form/Form.props.interface";
import {FormInterface} from "@/views/Admin/Events/Event/Promocodes/Form/Form.interface";
import DatePicker from "@/views/Admin/Events/Event/Promocodes/Form/components/DatePicker";
import AttachedEvents from "@/views/Admin/Events/Event/Promocodes/AttachedEvents/AttachedEvents";
import {normalizeBulkForm, normalizeForm} from "./Form.methods";
import {useLocation, useParams} from 'react-router-dom';
import ChevronIcon from "uikit/src/assets/icons/16/chevron.svg";
import {useSelector} from "react-redux";
import {generateHash} from "@/utils/generateHash";
import JSONAPI from "@/utils/JSONAPI/JSONAPI";
import {PromocodesService} from "@/services/PromocodesService/PromocodesService";
import {PromocodeInterface} from "@/views/Admin/Events/Event/Promocodes/Promocode/Promocode.interface";
import {
    createGroupResource,
    updateGroupResource
} from "@/views/Admin/Events/Event/Promocodes/Promocode/Promocode.methods";
import scrollToElement from "@/utils/scrollToElement";
import {useQueryRouter} from "@/hooks/useQueryRouter/useQueryRouter";
import {PromocodeGroupsService} from "@/services/PromocodeGroupsService/PromocodeGroupsService";
import {BULK_CONCURRENCY, promisePool, withRetry} from "@/views/Admin/Events/Event/Promocodes/Form/PromisePool";
import {hasEmoji} from "@/views/Admin/Events/Event/Promocodes/Form/ValidateEmoji";

const Form = ({data, onChange, onRefresh}: FormPropsInterface) => {
    const {search, pathname} = useLocation();
    const isCreateByUrl = useMemo(() => new URLSearchParams(search).get('id') === '-1', [search]);
    const {event_id, id} = useParams();
    const eventId = Number(event_id) || Number(id);
    const toast = useToast();
    const event = useSelector(state => state.admin.event);
    const ownerId = useRef(null);
    const {remove, replace} = useQueryRouter()
    const {promocodes, groups} = data;
    const editedPromo = promocodes.data.find(p => p.isEdit);
    const editedGroup = groups.data.find(g => g.isEdit);
    const isGroup = Boolean(editedGroup);
    const title = `Настройка ${isGroup ? 'группы промокодов' : 'промокода'}`;
    const undefinedName = 'Разные значения';
    const typeEvent = useMemo(() => {
        if (pathname.includes("/events/")) return "events";
        if (pathname.includes("/courses/")) return "courses";
        if (pathname.includes("/subscribe/")) return "subscribes"; // для будущих подписок
        return null;
    }, [pathname]);

    // активная группа: либо редактируемая группа, либо группа у редактируемого промокода
    const activeGroupId = editedGroup?.id ?? editedPromo?.groupId ?? null;

    // промокоды берём из самой группы при редактировании списка промокодов
    const initialPromocodes: PromocodeInterface[] =
        isGroup && activeGroupId
            ? (groups.data.find(g => String(g.id) === String(activeGroupId))?.promocodes ?? [])
            : (editedPromo?.groupId
                ? promocodes.data.filter(p => String(p.groupId) === String(editedPromo.groupId))
                : []);

    const [form, setForm] = useState<FormInterface>(() => {
        const isEdit = Boolean(editedPromo?.id || editedGroup?.id);

        const baseForm = isGroup && isEdit
            ? normalizeBulkForm(initialPromocodes)
            : normalizeForm(editedPromo || editedGroup, data.groups.data);

        baseForm.fields.eventId.value = eventId;
        baseForm.fields.groupId.variants = {};

        // выбранная группа
        if (activeGroupId) {
            baseForm.fields.groupId.value = String(activeGroupId);
        }

        if (isGroup) {
            baseForm.fields.promocodes.value = initialPromocodes.map(promo => ({
                id: promo.id,
                code: promo.promocode,
                error: null,
            }));
        }

        return baseForm;
    });

    const createdCodesRef = useRef<Set<string>>(new Set()); // какие промокоды были успешно созданы
    const shouldSubmitRef = useRef(false); // после setForm нужно будет вызвать сабмит
    const hasSubmittedAfterGroupCreateRef = useRef(false); //от бесконечного цикла при сменке значений form
    const containerRef = useRef(null)
    const current = document.querySelector('div[data-container]')

    const [progress, setProgress] = useState<number>(0);
    const [showProgress, setShowProgress] = useState<boolean>(false);

    // управлять прогрессом пачки
    const makeProgressBumper = (total: number) => {
        let done = 0;
        setShowProgress(true);
        setProgress(0);

        // если нечего обрабатывать — мгновенно скрываем
        if (total === 0) {
            setProgress(100);
            setShowProgress(false);
        }

        return () => {
            done += 1;
            const percent = Math.round((done / Math.max(total, 1)) * 100);
            setProgress(percent);
            if (percent >= 100) {
                // небольшая пауза, чтобы пользователь увидел «100%»
                setTimeout(() => setShowProgress(false), 200);
            }
        };
    };

    useEffect(() => {
        setForm(prev => ({
            ...prev,
            fields: {
                ...prev.fields,
                groupId: {...prev.fields.groupId, isLoading: true}
            }
        }));
        getUser();
        getGroups();
        scrollToElement(containerRef, {position: 'top', offset: -100});
    }, []);

    useEffect(() => {
        if (
            shouldSubmitRef.current &&
            form.fields.groupId.value &&
            !hasSubmittedAfterGroupCreateRef.current
        ) {
            shouldSubmitRef.current = false;
            hasSubmittedAfterGroupCreateRef.current = true;

            handleSubmit(new Event('submit'));
        }
    }, [form.fields.groupId.value]);

    useEffect(() => {
        //сбрасываем сохр. промокоды
        createdCodesRef.current.clear();
    }, [editedPromo, editedGroup]);

    const getUser = async () => {
        try {
            const user = await PromocodesService.getUser()
            ownerId.current = user.properties.owner_id;
        } catch (e) {
            console.error(e)
        }
    }

    const getGroups = async () => {
        try {
            const res = await PromocodeGroupsService.GetList({
                page: {size: 1000, page: 1},
                filter: {eventIds: [eventId]}
            });
            const items = Array.isArray(res?.data) ? res.data : [];
            const byId: Record<string, any> = {};
            for (const g of items) if (g?.id) byId[g.id] = g;

            setForm(prev => ({
                ...prev,
                fields: {
                    ...prev.fields,
                    groupId: {
                        ...prev.fields.groupId,
                        variants: {...prev.fields.groupId.variants, ...byId},
                    }
                }
            }));
        } catch (e) {
            console.error('Ошибка загрузки групп', e);
        } finally {
            setForm(prev => ({
                ...prev,
                fields: {
                    ...prev.fields,
                    groupId: {...prev.fields.groupId, isLoading: false}
                }
            }));
        }
    };

    const handleCancel = (e) => {
        e.preventDefault();
        scrollToElement({current}, {position: 'top', offset: -100})
        remove(['id'])
        onChange(prev => ({
            ...prev,
            promocodes: {
                ...prev.promocodes,
                data: prev.promocodes.data.map(p => ({...p, isEdit: false})).filter(p => p.hash)
            },
            groups: {
                ...prev.groups,
                data: prev.groups.data.map(g => ({...g, isEdit: false})).filter(g => g.hash)
            }
        }))
    }

    const handleSuccess = async ({event, response}) => {
        event.preventDefault();
        onRefresh()
        setForm(prev => ({...prev, isFormChanged: false, isLoading: false}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setForm(prev => ({...prev, isLoading: true}));

        if (validateForm()) {
            setForm(prev => ({...prev, isLoading: false}));
            return;
        }

        const freshForm = await handleGroupCreationIfNeeded();

        try {
            // === ГРУППОВОЙ РЕЖИМ ===
            if (isGroup) {
                if (isCreateByUrl) {
                    // создаём новый список промокодов — форму НЕ закрываем
                    await handleListCreate(e, freshForm);
                    // при успешном создании можно заменить -1 на id группы, чтобы дальше шли апдейты
                    if (freshForm.fields.groupId.value) {
                        replace({id: freshForm.fields.groupId.value});
                    }
                    return;
                } else {
                    // редактируем существующий список — после сохранения закрываем форму
                    await handleListUpdate(e, freshForm);
                    remove(['id']);
                    return;
                }
            }

            // === ОДИНОЧНЫЙ ПРОМОКОД ===
            if (isCreateByUrl) {
                // создаём — остаёмся на форме, меняем -1 на id созданного промокода
                const response = await PromocodesService.Create({data: freshForm, typeEvent: typeEvent});
                toast({type: 'success', message: 'Успешно сохранено'});
                handleSuccess({event: e, response});
                replace({id: response.data.data.id});
            } else {
                // редактируем — после сохранения закрываем форму
                const response = await PromocodesService.Update({
                    data: freshForm,
                    id: String(editedPromo.id),
                    typeEvent: typeEvent
                });
                toast({type: 'success', message: 'Успешно сохранено'});
                handleSuccess({event: e, response});
                remove(['id']);
            }
        } catch (e) {
            if (e.response?.status === 422) {
                handleValidateForm(e.response.data.errors);
            }
            console.error('Ошибка при сохранении:', e.message || e);
        } finally {
            setForm(prev => ({...prev, isLoading: false}));
        }
    };


    const handleGroupCreationIfNeeded = async (): Promise<FormInterface> => {
        const groupId = form.fields.groupId;
        const {variants, value} = groupId;

        const isHash = !/^\d+$/.test(value);
        const groupData = variants?.[value];
        if (!groupData) return form;

        const fn = isHash ? createGroupResource : updateGroupResource;
        const args = isHash
            ? [groupData.name, eventId, ownerId.current, typeEvent]
            : [groupData.name, eventId, ownerId.current, value];
        try {
            const res = await fn(...args);
            const newGroupData = await JSONAPI.deserialize(res.data);
            const newId = res.data.data.id;

            const updatedForm: FormInterface = {
                ...form,
                fields: {
                    ...form.fields,
                    groupId: {
                        ...form.fields.groupId,
                        value: newId,
                        variants: {
                            ...form.fields.groupId.variants,
                            [newId]: {
                                ...newGroupData,
                                id: newId,
                            },
                        },
                        isChanged: true,
                        error: null,
                    }
                }
            };

            // удаляем старый hash-ключ
            delete updatedForm.fields.groupId.variants[value];

            setForm(prev => ({
                ...updatedForm,
                isFormChanged: true,
            }));

            onChange(prev => ({
                ...prev,
                groups: {
                    ...prev.groups,
                    data: [...prev.groups.data, {...newGroupData, hash: generateHash(), isEdit: false}]
                }
            }));

            return updatedForm;
        } catch (e) {
            console.error('Ошибка при создании/обновлении группы', e);
            return form;
        }
    };


    const validateForm = (): boolean => {
        const updatedFields = {...form.fields};
        let hasError = false;

        for (const key in updatedFields) {
            const field = updatedFields[key];
            if (isGroup && key === 'promocode') continue;

            if (field?.isRequired && (field.value === '' || field.value === null || field.value === undefined)) {
                updatedFields[key] = {...field, error: `Поле ${field.label} обязательно для заполнения`};
                hasError = true;
            }
        }

        //если typeId === 'percent', size не должен превышать 100
        const typeId = updatedFields.typeId?.value;
        const rawSize = updatedFields.size?.value;

        // игнорируем спец-значения '—', пусто и т.п.
        if (typeId === 'percent' && rawSize !== '—' && rawSize !== '' && rawSize !== null && rawSize !== undefined) {
            const sizeNum = Number(rawSize);
            if (!Number.isNaN(sizeNum) && sizeNum > 100) {
                updatedFields.size = {
                    ...updatedFields.size,
                    error: 'Скидка не может превышать 100%',
                };
                hasError = true;
            }
        }

        if (hasError) {
            setForm(prev => ({...prev, fields: updatedFields}));
            return true;
        }
        return false;
    }

    const handleListCreate = async (e, freshForm: FormInterface) => {
        const codes = freshForm.fields.promocodes.value;
        const errors: string[] = [];

        setForm(prev => ({...prev, isLoading: true}));

        // только реально новые (ещё не созданные)
        const payloads = codes
            .filter(c => !createdCodesRef.current.has(c.code))
            .map(c => ({codeObj: c, form: prepareClonedForm(freshForm, c.code)}));

        const createdPairs: Array<{ code: string; id: number }> = [];
        const bump = makeProgressBumper(payloads.length);

        try {
            await promisePool(payloads, BULK_CONCURRENCY, async ({codeObj, form}) => {
                try {
                    const resp = await withRetry(() => PromocodesService.Create({data: form, typeEvent: typeEvent}));
                    const newId = Number(resp?.data?.data?.id);
                    if (Number.isFinite(newId)) {
                        createdPairs.push({code: codeObj.code, id: newId});
                        createdCodesRef.current.add(codeObj.code);
                    }
                } catch (err) {
                    handleGroupedError(err, codeObj.code, errors);
                } finally {
                    bump(); // <= считаем каждый завершённый таск
                }
            });

            if (createdPairs.length) {
                setForm(prev => ({
                    ...prev,
                    fields: {
                        ...prev.fields,
                        promocodes: {
                            ...prev.fields.promocodes,
                            value: prev.fields.promocodes.value.map(chip => {
                                const found = createdPairs.find(p => p.code === chip.code);
                                return found ? {...chip, id: found.id, error: ''} : chip;
                            }),
                        },
                    },
                }));
            }

            setForm(prev => ({...prev, isLoading: false}));

            if (errors.length) {
                toast({type: 'error', message: `Ошибки при создании некоторых промокодов:\n${errors.join('\n')}`});
            } else {
                if (freshForm.fields.groupId.value) replace({id: freshForm.fields.groupId.value});
                toast({type: 'success', message: `Создано ${codes.length} промокодов`});
                handleSuccess({event: e, response: {data: {}}});
            }
        } catch (err) {
            setForm(prev => ({...prev, isLoading: false}));
            toast({type: 'error', message: 'Произошла ошибка при пакетном создании'});
            console.error(err);
        }
    };


    const handleListUpdate = async (e, freshForm: FormInterface) => {
        const codes = freshForm.fields.promocodes.value;
        const errors: string[] = [];

        setForm(prev => ({...prev, isLoading: true}));

        const payloads = codes.map(c => ({
            codeObj: c,
            isNew: c.id == null,
            form: prepareClonedForm(freshForm, c.code, c.id),
        }));

        const bump = makeProgressBumper(payloads.length);

        try {
            await promisePool(payloads, BULK_CONCURRENCY, async ({codeObj, isNew, form}) => {
                try {
                    if (isNew) {
                        const resp = await withRetry(() => PromocodesService.Create({
                            data: form,
                            typeEvent: typeEvent
                        }));
                        const newId = Number(resp?.data?.data?.id);
                        if (Number.isFinite(newId)) {
                            // подставим id в чип прямо по ходу (если нужно — можно собрать пары и сделать одним setState)
                            setForm(prev => ({
                                ...prev,
                                fields: {
                                    ...prev.fields,
                                    promocodes: {
                                        ...prev.fields.promocodes,
                                        value: prev.fields.promocodes.value.map(chip =>
                                            chip.code === codeObj.code ? {...chip, id: newId, error: ''} : chip
                                        ),
                                    },
                                },
                            }));
                        }
                        createdCodesRef.current.add(codeObj.code);
                    } else {
                        await withRetry(() => PromocodesService.Update({
                            data: form,
                            id: String(codeObj.id),
                            typeEvent
                        }));
                    }
                } catch (err) {
                    handleGroupedError(err, codeObj.code, errors);
                } finally {
                    bump(); // <= прогресс
                }
            });

            setForm(prev => ({...prev, isLoading: false}));

            if (errors.length) {
                toast({type: 'error', message: `Ошибки при редактировании:\n${errors.join('\n')}`});
            } else {
                toast({type: 'success', message: `Обновлено ${codes.length} промокодов`});
                handleSuccess({event: e, response: {data: {}}});
            }
        } catch (err) {
            setForm(prev => ({...prev, isLoading: false}));
            toast({type: 'error', message: 'Произошла ошибка при пакетном обновлении'});
            console.error(err);
        }
    };


    const prepareClonedForm = (
        baseForm: FormInterface,
        code: string,
        id?: number | string
    ): FormInterface => {
        let sourcePromocode: PromocodeInterface | undefined;

        if (id) {
            sourcePromocode = initialPromocodes.find(p => p.id === id);
        } else {
            // берем промокод из группы (по которому кликали)
            sourcePromocode = initialPromocodes.find(p => p.id === editedPromo?.id);
        }

        const clonedFields = Object.fromEntries(
            Object.entries(baseForm.fields).map(([key, field]) => {
                let value = field.value;

                if (!field.isChanged && sourcePromocode && key in sourcePromocode) {
                    value = sourcePromocode[key as keyof PromocodeInterface];
                }

                if (key === 'promocode') {
                    value = code;
                }

                if (value === '—' || value === undefined) {
                    value = null;
                }

                return [key, {...field, value}];
            })
        );

        return {
            ...baseForm,
            fields: clonedFields as FormInterface['fields'],
        };
    };


    const handleGroupedError = (
        error: any,
        code: string,
        errors: string[]
    ) => {
        if (error.response?.status === 422) {
            const backendErrors = error.response.data?.errors ?? [];
            const detail = backendErrors.map((err: any) => err.detail).join('; ') || 'Неизвестная ошибка';

            setForm(prev => ({
                ...prev,
                fields: {
                    ...prev.fields,
                    promocodes: {
                        ...prev.fields.promocodes,
                        value: prev.fields.promocodes.value.map((c: any) =>
                            c.code === code ? {...c, error: detail} : c
                        ),
                    },
                },
            }));

            errors.push(`Промокод "${code}": ${detail}`);
        } else {
            errors.push(`Промокод "${code}": ${error.message || 'Неизвестная ошибка'}`);
        }
    };

    const handleValidateForm = (errors: any[]) => {
        const updatedForm = {...form, fields: {...form.fields}};

        const errorMap: Record<string, string[]> = {};

        for (const err of errors) {
            const key = err.source?.pointer?.replace('/data/attributes/', '');
            if (!key) continue;

            if (!errorMap[key]) {
                errorMap[key] = [];
            }

            errorMap[key].push(err.detail);
        }

        for (const key in errorMap) {
            if (!updatedForm.fields[key]) continue;

            updatedForm.fields[key] = {
                ...updatedForm.fields[key],
                error: errorMap[key].join(', '),
            };
        }

        setForm(updatedForm);
    }

    const handleChange = (
        fieldName: keyof FormInterface['fields'],
        rawValue: FormInterface['fields'][typeof fieldName]['value']
    ) => {
        setForm(prev => {
            let value = rawValue;
            let error: string | null = null;

            // Не валидируем спец-маркер «разные значения»
            const isDash = value === '—';
            if (!isDash && typeof value === 'string') {
                if (hasEmoji(value)) {
                    error = 'Недопустимые символы (эмодзи)';
                }
            }

            const nextFields = {
                ...prev.fields,
                [fieldName]: {
                    ...prev.fields[fieldName],
                    value,
                    error,
                    isChanged: true,
                },
            };

            // Если меняем тип скидки - очищаем ошибку у size
            if (fieldName === 'typeId') {
                nextFields.size = {
                    ...prev.fields.size,
                    error: null,
                };
            }

            return {
                ...prev,
                isFormChanged: true,
                fields: nextFields,
            };
        });
    };

    const hasErrors = Object.values(form.fields).some((f: any) => Boolean(f?.error));


    const handleNonNegativeNumberChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        field: string,
        handleChange: (field: string, value: string | number) => void
    ) => {
        let raw = String(e.target.value).trim();

        // Разрешаем Infinity как отдельное значение
        if (raw.toLowerCase() === 'infinity') {
            handleChange(field, Infinity);
            return;
        }

        // Удаляем все символы, кроме цифр и точки
        raw = raw.replace(/[^0-9.]/g, '');

        // Удаляем лишние точки, оставляя только первую
        const parts = raw.split('.');
        if (parts.length > 2) {
            raw = parts[0] + '.' + parts.slice(1).join('');
        }

        // Если начинается с точки, добавляем 0
        if (raw.startsWith('.')) {
            raw = '0' + raw;
        }

        const num = Number(raw);

        // Только пустая строка или число > 0
        if (raw === '' || (!isNaN(num) && num > 0)) {
            handleChange(field, raw);
        }
    };


    return (
        <>
            {showProgress && <Progress percent={progress}/>}
            <form
                data-testid="promo-form"
                ref={containerRef}
                className={styles.container}
                onSubmit={handleSubmit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                    }
                }}>
                <div className={styles.btnBack} onClick={handleCancel}>
                    <ChevronIcon onClick={handleCancel}/> назад
                </div>

                <h2 className={styles.heading} data-testid="form-title">{title}</h2>

                <GroupRow
                    isGroup={isGroup}
                    form={form}
                    setForm={setForm}
                    handleChange={handleChange}
                    undefinedName={undefinedName}
                />

                <div className={styles.row}>
                    <div className={styles.fieldSingle}>
                        <Input
                            data-testid="count-input"
                            value={form.fields.count.value}
                            label={form.fields.count.label ?? ''}
                            placeholder={form.fields.count.value === '—' ? undefinedName : form.fields.count.placeholder ?? ''}
                            error={form.fields.count.error ?? ''}
                            disabled={form.fields.count.isDisabled ?? false}
                            onChange={(e) => handleNonNegativeNumberChange(e, 'count', handleChange)}
                            size="small"
                            isInfinity
                            type="number"
                            inputMode="decimal"
                            min={1}
                            pattern="^[1-9]\\d*(\\.\\d+)?$"
                        />
                    </div>
                    <div className={styles.fieldDouble}>
                        <Input
                            data-testid="size-input"
                            value={form.fields.size.value}
                            label={form.fields.size.label ?? ''}
                            placeholder={form.fields.size.value === '—' ? undefinedName : form.fields.size.placeholder ?? ''}
                            error={form.fields.size.error ?? ''}
                            disabled={form.fields.size.isDisabled ?? false}
                            onChange={(e) => handleNonNegativeNumberChange(e, 'size', handleChange)}
                            type={'number'}
                            inputMode="decimal"
                            size="small"
                            min={1}
                            pattern="^[1-9]\\d*(\\.\\d+)?$"
                        />

                        <Select
                            data-testid="typeId-select"
                            label="Единицы"
                            size="small"
                            placeholder={form.fields.typeId.value === '—' ? undefinedName : ''}
                            data={{fixed: 'руб.', percent: '%'}}
                            value={form.fields.typeId.value === '—' ? null : form.fields.typeId.value}
                            onChange={(val) => handleChange('typeId', val)}
                        />
                    </div>
                </div>

                <div className={styles.row}>

                    <div className={styles.fieldDouble}>
                        <DatePicker
                            data-testid="from-datepicker-from"
                            undefinedName={undefinedName}
                            label="Старт"
                            utc={event.timezone.utc || event.utc}
                            value={form.fields.from.value}
                            error={form.fields.from.error}
                            onChange={(val) => handleChange('from', val)}
                        />
                        <DatePicker
                            data-testid="from-datepicker-till"
                            undefinedName={undefinedName}
                            label="Окончание"
                            utc={event.timezone.utc || event.utc}
                            value={form.fields.till.value}
                            error={form.fields.till.error}
                            onChange={(val) => handleChange('till', val)}
                        />
                    </div>
                    <div className={styles.timezoneCol}>
                        <div className={styles.timezoneLabel}>
                            Часовой пояс
                        </div>
                        <div className={styles.timezoneValue}>
                            {event.timezone.utc || event.utc}: {event.timezone.name || event.datesTimezone}
                        </div>
                    </div>
                </div>

                <div className={styles.row}>
                    <div style={{width: "100%"}}>
                        <h3>Количество билетов в заказе</h3>
                        <div className={styles.fieldDouble}>
                            <Input
                                data-testid="minticket-input"
                                value={form.fields.minticket.value}
                                label={form.fields.minticket.label ?? ''}
                                placeholder={form.fields.minticket.value === '—' ? undefinedName : form.fields.minticket.placeholder ?? ''}
                                error={form.fields.minticket.error ?? ''}
                                disabled={form.fields.minticket.isDisabled ?? false}
                                onChange={(e) => handleNonNegativeNumberChange(e, 'minticket', handleChange)}
                                type="number"
                                size="small"
                                min={0}
                                pattern="^\d*(\.\d+)?$"
                            />

                            <Input
                                data-testid="maxticket-input"
                                value={form.fields.maxticket.value}
                                label={form.fields.maxticket.label ?? ''}
                                placeholder={form.fields.maxticket.value === '—' ? undefinedName : form.fields.maxticket.placeholder ?? ''}
                                error={form.fields.maxticket.error ?? ''}
                                disabled={form.fields.maxticket.isDisabled ?? false}
                                onChange={(e) => handleNonNegativeNumberChange(e, 'maxticket', handleChange)}
                                type="number"
                                size="small"
                                min={0}
                                isInfinity
                                pattern="^\d*(\.\d+)?$"
                            />

                        </div>
                    </div>

                    <div style={{width: "100%"}}>
                        <h3>Сумма заказа, руб.</h3>
                        <div className={styles.fieldDouble}>
                            <Input
                                data-testid="minsum-input"
                                value={form.fields.minsum.value}
                                label={form.fields.minsum.label ?? ''}
                                placeholder={form.fields.minsum.value === '—' ? undefinedName : form.fields.minsum.placeholder ?? ''}
                                error={form.fields.minsum.error ?? ''}
                                disabled={form.fields.minsum.isDisabled ?? false}
                                onChange={(e) => handleNonNegativeNumberChange(e, 'minsum', handleChange)}
                                type="number"
                                size="small"
                                min={0}
                                pattern="^\d*(\.\d+)?$"
                            />
                            <Input
                                data-testid="maxsum-input"
                                value={form.fields.maxsum.value}
                                label={form.fields.maxsum.label ?? ''}
                                placeholder={form.fields.maxsum.value === '—' ? undefinedName : form.fields.maxsum.placeholder ?? ''}
                                error={form.fields.maxsum.error ?? ''}
                                disabled={form.fields.maxsum.isDisabled ?? false}
                                onChange={(e) => handleNonNegativeNumberChange(e, 'maxsum', handleChange)}
                                type="number"
                                size="small"
                                min={0}
                                isInfinity
                                pattern="^\d*(\.\d+)?$"
                            />
                        </div>
                    </div>
                </div>

                <SwitcherGroup
                    fields={form.fields}
                    handleChange={handleChange}
                />

                <div className={styles.actions}>
                    <Button
                        type={'ghost'}
                        theme={'tertiary'}
                        size={'small'}
                        data-testid={'cancel-button'}
                        disabled={form.isLoading}
                        onClick={handleCancel}>
                        Отмена
                    </Button>
                    <Button
                        type={'primary'}
                        theme={'default'}
                        size={'small'}
                        htmlType="submit"
                        disabled={form.isLoading || !form.isFormChanged || hasErrors}
                        data-testid={'submit-button'}>
                        {form.isLoading ? 'Сохранение…' : 'Сохранить'}
                    </Button>
                </div>
            </form>

            {editedPromo && (
                <AttachedEvents
                    initialPromocodes={initialPromocodes}
                    data={editedPromo as PromocodeInterface}
                    onChange={onChange}
                />
            )}
        </>
    )
};

export default Form;
